/**
 * Patient Domain Service
 *
 * Domain service for patient-specific operations in the BiteWise healthcare provider platform.
 * Handles patient registration, profile management, authentication integration, and patient-specific
 * data operations with enhanced error handling, retry logic, and request deduplication.
 *
 * Replaces direct auth.signUp calls for patient registration with dedicated patient domain logic.
 *
 * @module services/domains/PatientDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager, getLoggerWithPrefix } from '../shared';

/**
 * Patient registration request data (frontend format - camelCase)
 */
export interface PatientRegistrationRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  marketingConsent?: boolean;
}

/**
 * Patient registration API request format (backend format - snake_case)
 */
interface PatientRegistrationApiRequest {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  role: 'patient';
  date_of_birth?: string;
  phone_number?: string;
  consent: {
    terms_of_service: boolean;
    privacy_policy: boolean;
    data_processing: boolean;
    marketing_communications: boolean;
  };
}

/**
 * Patient registration API response format (backend format - snake_case)
 */
interface PatientRegistrationApiResponse {
  status: string;
  user_id?: string;
  patient_id?: string;
  message: string;
  user?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_active: boolean;
    is_verified: boolean;
    created_at: string;
    updated_at: string;
  };
  form_fields?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Patient registration response (frontend format - camelCase)
 */
export interface PatientRegistrationResponse {
  success: boolean;
  userId?: string;
  patientId?: string;
  message: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    updatedAt: string;
  };
  fieldErrors?: Record<string, string>;
}

/**
 * Patient profile data
 */
export interface PatientProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: 'patient';
  dateOfBirth?: string;
  phoneNumber?: string;
  profilePhotoUrl?: string;
  medicalConditions?: string[];
  allergies?: string[];
  currentMedications?: string[];
  emergencyContact?: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Patient profile API response format (backend format - snake_case)
 */
interface PatientProfileApiResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  phone_number?: string;
  photo?: string;
  profile_photo_url?: string;
  medical_conditions?: string[];
  allergies?: string[];
  current_medications?: string[];
  emergency_contact?: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Patient profile update request
 */
export interface PatientProfileRequest {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
}

/**
 * Patient profile response
 */
export interface PatientProfileResponse {
  success: boolean;
  profile: PatientProfile;
  message?: string;
}

/**
 * Patient referral data
 */
export interface PatientReferral {
  id: string;
  status: string;
  dieticianName?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Patient appointment data
 */
export interface PatientAppointment {
  id: string;
  date: string;
  time: string;
  dieticianName: string;
  status: string;
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  fieldErrors: Record<string, string>;
}

/**
 * Patient domain-specific error class
 */
export class PatientApiError extends BaseApiError {
  /** The operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'PatientApiError';
    this.operation = operation;
  }
}

/**
 * API endpoints for patient operations
 */
const ENDPOINTS = {
  SIGNUP: '/auth/signup',
  PROFILE: '/users/me/patient-profile',
  REFERRALS: '/referrals',
  APPOINTMENTS: '/appointments/calendly',
} as const;

/**
 * PatientDomain - Patient Management Domain Service
 *
 * Handles all patient-specific operations including registration,
 * profile management, authentication, and patient-specific data operations.
 * Extends BaseDomain to leverage shared infrastructure.
 */
export class PatientDomain extends BaseDomain<PatientApiError> {
  constructor() {
    const apiClient = new ApiClient<PatientApiError>(
      (message, status, fieldErrors, code) =>
        new PatientApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }

  protected getDomainName(): string {
    return 'Patient';
  }

  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): PatientApiError {
    return new PatientApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Transform patient registration form data to API request format
   * Converts camelCase to snake_case following established patterns
   */
  private transformRegistrationRequest(
    formData: PatientRegistrationRequest
  ): PatientRegistrationApiRequest {
    return {
      // Authentication fields
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
      confirm_password: formData.password, // Use same password for confirmation

      // Patient-specific fields
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      role: 'patient' as const,

      // Optional fields
      ...(formData.dateOfBirth && { date_of_birth: formData.dateOfBirth }),
      ...(formData.phoneNumber && { phone_number: formData.phoneNumber }),

      // Consent tracking
      consent: {
        terms_of_service: true,
        privacy_policy: true,
        data_processing: true,
        marketing_communications: formData.marketingConsent ?? true
      }
    };
  }

  /**
   * Transform API response to frontend format
   * Converts snake_case to camelCase following established patterns
   */
  private transformRegistrationResponse(
    apiResponse: PatientRegistrationApiResponse
  ): PatientRegistrationResponse {
    return {
      success: apiResponse.status === 'OK',
      userId: apiResponse.user_id,
      patientId: apiResponse.patient_id,
      message: apiResponse.message,

      // Transform user data if present
      user: apiResponse.user ? {
        id: apiResponse.user.id,
        email: apiResponse.user.email,
        firstName: apiResponse.user.first_name,
        lastName: apiResponse.user.last_name,
        role: apiResponse.user.role,
        isActive: apiResponse.user.is_active,
        isVerified: apiResponse.user.is_verified,
        createdAt: apiResponse.user.created_at,
        updatedAt: apiResponse.user.updated_at
      } : undefined,

      // Handle field errors
      fieldErrors: apiResponse.form_fields?.reduce((acc, field) => {
        acc[field.id] = field.error;
        return acc;
      }, {} as Record<string, string>)
    };
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate age requirement (minimum 13 years old)
   */
  private isValidAge(dateOfBirth: string): boolean {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 13;
    }

    return age >= 13;
  }

  /**
   * Register a new patient
   *
   * @param registrationData - Patient registration form data
   * @returns Promise resolving to registration response
   * @throws PatientApiError for registration failures
   */
  async signUp(registrationData: PatientRegistrationRequest): Promise<PatientRegistrationResponse> {
    return this.executeWithRetry(async () => {
      try {
        // Pre-validation
        const validationResult = await this.validateRegistrationData(registrationData);
        if (!validationResult.isValid) {
          throw this.createError(
            'signUp',
            'Registration data validation failed',
            400,
            validationResult.fieldErrors,
            'VALIDATION_ERROR'
          );
        }

        // Transform and submit
        const apiRequest = this.transformRegistrationRequest(registrationData);
        const response = await this.apiClient.post<PatientRegistrationApiResponse>(
          ENDPOINTS.SIGNUP,
          apiRequest
        );

        return this.transformRegistrationResponse(response);

      } catch (error) {
        if (error instanceof PatientApiError) {
          // Handle specific patient registration errors
          if (error.status === 409) {
            throw this.createError(
              'signUp',
              'An account with this email already exists. Please use a different email or try logging in.',
              409,
              { email: 'Email already registered' },
              'EMAIL_EXISTS'
            );
          }

          if (error.status === 422) {
            throw this.createError(
              'signUp',
              'Invalid patient registration data. Please check your information and try again.',
              422,
              error.fieldErrors,
              'INVALID_DATA'
            );
          }

          if (error.status === 429) {
            throw this.createError(
              'signUp',
              'Too many registration attempts. Please wait a few minutes before trying again.',
              429,
              undefined,
              'RATE_LIMITED'
            );
          }
        }

        throw error;
      }
    });
  }

  /**
   * Validate patient registration data
   *
   * @param data - Registration data to validate
   * @returns Promise resolving to validation result
   */
  async validateRegistrationData(data: PatientRegistrationRequest): Promise<ValidationResult> {
    const errors: Record<string, string> = {};

    // Email validation
    if (!data.email || !this.isValidEmail(data.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!data.password || data.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }

    // Name validation
    if (!data.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!data.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }

    // Age validation
    if (data.dateOfBirth) {
      const birthDate = new Date(data.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust age if birthday hasn't occurred this year
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ? age - 1
        : age;

      if (actualAge < 13) {
        errors.dateOfBirth = 'Patient must be at least 13 years old';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      fieldErrors: errors
    };
  }


  /**
   * Complete patient profile after registration
   *
   * @param profileData - Profile completion data
   * @returns Promise resolving to profile response
   */
  async completeProfile(profileData: PatientProfileRequest): Promise<PatientProfileResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.put<PatientProfileApiResponse>(
          ENDPOINTS.PROFILE,
          {
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            date_of_birth: profileData.dateOfBirth,
            phone_number: profileData.phoneNumber
          }
        );

        return {
          success: true,
          profile: {
            id: response.id,
            email: response.email,
            firstName: response.first_name,
            lastName: response.last_name,
            role: 'patient' as const,
            dateOfBirth: response.date_of_birth,
            phoneNumber: response.phone_number,
            profilePhotoUrl: response.profile_photo_url,
            medicalConditions: response.medical_conditions,
            allergies: response.allergies,
            currentMedications: response.current_medications,
            emergencyContact: response.emergency_contact,
            isActive: response.is_active,
            isVerified: response.is_verified,
            createdAt: response.created_at,
            updatedAt: response.updated_at
          }
        };
      } catch (error) {
        throw this.createError(
          'completeProfile',
          'Failed to complete patient profile',
          error instanceof PatientApiError ? error.status : 500,
          error instanceof PatientApiError ? error.fieldErrors : undefined,
          'PROFILE_UPDATE_FAILED'
        );
      }
    });
  }

  /**
   * Get patient profile
   *
   * @returns Promise resolving to patient profile
   */
  async getProfile(): Promise<PatientProfile> {
    const logger = getLoggerWithPrefix('PatientDomain');

    return this.executeWithDeduplication(
      'patient-profile',
      async () => {
        try {
          const response = await this.apiClient.get<PatientProfileApiResponse>(ENDPOINTS.PROFILE);
          logger.debug('response: ', response);
          logger.debug('response.photo: ', response.photo);

          // Transform snake_case to camelCase - only return expected fields
          return {
            id: response.id,
            email: response.email,
            firstName: response.first_name,
            lastName: response.last_name,
            dateOfBirth: response.date_of_birth,
            phoneNumber: response.phone_number,
            profilePhotoUrl: response.photo || response.profile_photo_url,
            isActive: response.is_active,
            isVerified: response.is_verified,
            createdAt: response.created_at,
            updatedAt: response.updated_at
          };
        } catch (error) {
          throw this.createError(
            'getProfile',
            'Failed to fetch patient profile',
            error instanceof PatientApiError ? error.status : 500,
            undefined,
            'PROFILE_FETCH_FAILED'
          );
        }
      }
    );
  }

  /**
   * Update patient profile
   *
   * @param profileData - Profile update data
   * @returns Promise resolving to updated profile
   */
  async updateProfile(profileData: Partial<PatientProfile>): Promise<PatientProfile> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.patch<PatientProfileApiResponse>(
          ENDPOINTS.PROFILE,
          {
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            date_of_birth: profileData.dateOfBirth,
            phone_number: profileData.phoneNumber
          }
        );

        return {
          id: response.id,
          email: response.email,
          firstName: response.first_name,
          lastName: response.last_name,
          role: 'patient' as const,
          dateOfBirth: response.date_of_birth,
          phoneNumber: response.phone_number,
          profilePhotoUrl: response.profile_photo_url,
          medicalConditions: response.medical_conditions,
          allergies: response.allergies,
          currentMedications: response.current_medications,
          emergencyContact: response.emergency_contact,
          isActive: response.is_active,
          isVerified: response.is_verified,
          createdAt: response.created_at,
          updatedAt: response.updated_at
        };
      } catch (error) {
        throw this.createError(
          'updateProfile',
          'Failed to update patient profile',
          error instanceof PatientApiError ? error.status : 500,
          error instanceof PatientApiError ? error.fieldErrors : undefined,
          'PROFILE_UPDATE_FAILED'
        );
      }
    });
  }


  /**
   * Get patient referral history
   *
   * @returns Promise resolving to referral history
   */
  async getReferralHistory(): Promise<PatientReferral[]> {
    return this.executeWithDeduplication(
      'patient-referrals',
      async () => {
        try {
          const response = await this.apiClient.get<PatientReferral[]>(ENDPOINTS.REFERRALS);
          return response;
        } catch (error) {
          throw this.createError(
            'getReferralHistory',
            'Failed to fetch referral history',
            error instanceof PatientApiError ? error.status : 500,
            undefined,
            'REFERRALS_FETCH_FAILED'
          );
        }
      }
    );
  }

  /**
   * Get active patient referral
   *
   * @returns Promise resolving to active referral or null
   */
  async getActiveReferral(): Promise<PatientReferral | null> {
    return this.executeWithDeduplication(
      'patient-active-referral',
      async () => {
        try {
          const referrals = await this.getReferralHistory();
          return referrals.find(referral => referral.status === 'active') || null;
        } catch (error) {
          throw this.createError(
            'getActiveReferral',
            'Failed to fetch active referral',
            error instanceof PatientApiError ? error.status : 500,
            undefined,
            'ACTIVE_REFERRAL_FETCH_FAILED'
          );
        }
      }
    );
  }

  /**
   * Get patient appointments
   *
   * @returns Promise resolving to appointments list
   */
  async getAppointments(): Promise<PatientAppointment[]> {
    return this.executeWithDeduplication(
      'patient-appointments',
      async () => {
        try {
          const response = await this.apiClient.get<PatientAppointment[]>(ENDPOINTS.APPOINTMENTS);
          return response;
        } catch (error) {
          throw this.createError(
            'getAppointments',
            'Failed to fetch appointments',
            error instanceof PatientApiError ? error.status : 500,
            undefined,
            'APPOINTMENTS_FETCH_FAILED'
          );
        }
      }
    );
  }

}

// Create and export singleton instance
export const patientDomain = new PatientDomain();